// Save as Values / Restore All Functions, on the EAV tab and the cell menu.
// LSEG's Save/Restore as Values is hidden for this user's Workspace (its menu
// entries need a Workspace app, SAVEASVALUES, 2026-11-01), so its behaviour
// could not be captured: this is EAV's own design.
// - Save as Values (selection / sheet / workbook): every cell whose formula
//   calls an EAV function becomes the value it shows. Ordinary formulas stay;
//   the cells an EAV formula wrote around itself are values already. A cell
//   still computing or showing an Excel error is left as a formula.
// - The formulas are kept in the workbook, in the add-in's document settings
//   (saved with the file, in no cell or sheet), so Restore works after the
//   workbook is closed and opened again.
// - Restore All Functions puts each formula back where the cell still holds
//   the value it was saved with; a cell changed since is left alone, said in
//   the panel, and kept for a later Restore.
//
// The flows only talk to a ValuesPort, so they are tested without Excel.

import type { FormulaScope, ScopedFormula } from "../lib/excelPort";
import { isExcelError } from "./insertChart";

/** A cell turned into its value, with what Restore needs: its formula and the value it was saved as. */
export interface SavedCell {
  sheet: string;
  row: number;
  col: number;
  formula: string;
  value: string | number | boolean;
}

/** What a cell holds now. `sheetGone`: no sheet has that name any more (deleted, renamed). */
export interface CellNow {
  formula: string;
  value: unknown;
  sheetGone?: boolean;
}

/** The key of the add-in's document setting that keeps the saved cells. */
export const SAVED_KEY = "eav.savedFormulas";

const cellKey = (c: { sheet: string; row: number; col: number }) => `${c.sheet}\u0000${c.row}\u0000${c.col}`;

/**
 * The EAV formula cells of `formulas` to turn into values, given what each
 * shows (`values[i]` for `formulas[i]`). Not saved: other formulas, and a
 * cell still computing or showing an Excel error (`unready`).
 */
export function cellsToSave(
  formulas: ScopedFormula[],
  values: unknown[],
  isEav: (formula: string) => boolean,
): { save: SavedCell[]; unready: number } {
  const save: SavedCell[] = [];
  let unready = 0;
  formulas.forEach((f, i) => {
    if (!isEav(f.formula)) return;
    const value = values[i];
    if (isExcelError(value) || !["string", "number", "boolean"].includes(typeof value)) {
      unready++;
      return;
    }
    save.push({ sheet: f.sheet, row: f.row, col: f.col, formula: f.formula, value: value as SavedCell["value"] });
  });
  return { save, unready };
}

/** The saved cells after saving `added`: a cell saved again keeps its newest formula and value. */
export function mergeSaved(saved: SavedCell[], added: SavedCell[]): SavedCell[] {
  const byCell = new Map(saved.map((c) => [cellKey(c), c]));
  for (const c of added) byCell.set(cellKey(c), c);
  return [...byCell.values()];
}

/**
 * Which saved cells Restore puts back (`now[i]` is what `saved[i]` holds now):
 * those still holding no formula and the very value they were saved as. The
 * others (`changed`: edited, cleared, given another formula, or their sheet
 * is gone) are left as they are.
 */
export function restorePlan(saved: SavedCell[], now: CellNow[]): { restore: SavedCell[]; changed: SavedCell[] } {
  const restore: SavedCell[] = [];
  const changed: SavedCell[] = [];
  saved.forEach((c, i) => {
    const n = now[i];
    if (n && !n.sheetGone && n.formula === "" && n.value === c.value) restore.push(c);
    else changed.push(c);
  });
  return { restore, changed };
}

/** A1 address of a 0-based cell. */
export function a1(row: number, col: number): string {
  let letters = "";
  for (let n = col + 1; n > 0; n = Math.floor((n - 1) / 26)) letters = String.fromCharCode(65 + ((n - 1) % 26)) + letters;
  return `${letters}${row + 1}`;
}

const SCOPE_TEXT: Record<FormulaScope, string> = { selection: "the selection", sheet: "the active sheet", workbook: "the workbook" };
const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** The Excel side of Save as Values and Restore. */
export interface ValuesPort {
  /** The formula cells of `scope` (excelPort's formulasIn). */
  formulasIn(scope: FormulaScope): Promise<ScopedFormula[]>;
  /** What each cell holds now, in order. */
  readCells(cells: { sheet: string; row: number; col: number }[]): Promise<CellNow[]>;
  /** Write each cell's value over its formula. */
  writeValues(cells: SavedCell[]): Promise<void>;
  /** Write each cell's formula back. */
  writeFormulas(cells: SavedCell[]): Promise<void>;
  /** The saved cells kept in the workbook ([] when none). */
  loadSaved(): SavedCell[];
  /** Keep `cells` in the workbook (they are saved with the file). */
  storeSaved(cells: SavedCell[]): Promise<void>;
}

/**
 * Save as Values: the EAV formulas of `scope` become their values, kept for
 * Restore. Returns the panel's text when there is something to say (nothing
 * to save, or cells left as formulas), else null: the cells show it.
 */
export async function saveAsValues(port: ValuesPort, scope: FormulaScope, isEav: (formula: string) => boolean): Promise<string | null> {
  const formulas = (await port.formulasIn(scope)).filter((f) => isEav(f.formula));
  if (!formulas.length) return `Save as Values: no EAV formula in ${SCOPE_TEXT[scope]}.`;
  const now = await port.readCells(formulas);
  const { save, unready } = cellsToSave(formulas, now.map((n) => n.value), isEav);
  if (save.length) {
    // Keep the formulas first: a failure after this loses no formula.
    await port.storeSaved(mergeSaved(port.loadSaved(), save));
    await port.writeValues(save);
  }
  if (!unready) return null;
  return (
    `Save as Values: ${plural(save.length, "formula")} saved as values; ${plural(unready, "cell")} still computing ` +
    `or showing an error ${unready === 1 ? "was" : "were"} left as ${unready === 1 ? "a formula" : "formulas"}.`
  );
}

/**
 * Restore All Functions: every saved cell that still shows its saved value
 * gets its formula back; the others stay as they are and stay saved. Returns
 * the panel's text when there is something to say, else null.
 */
export async function restoreFunctions(port: ValuesPort): Promise<string | null> {
  const saved = port.loadSaved();
  if (!saved.length) return "Restore All Functions: no formula of this workbook was saved as values.";
  const { restore, changed } = restorePlan(saved, await port.readCells(saved));
  if (restore.length) {
    await port.writeFormulas(restore);
    await port.storeSaved(changed);
  }
  if (!changed.length) return null;
  const listed = changed.slice(0, 10).map((c) => `${c.sheet}!${a1(c.row, c.col)}`).join(", ");
  const more = changed.length > 10 ? ` and ${changed.length - 10} more` : "";
  return (
    `Restore All Functions: ${plural(restore.length, "formula")} restored; ${plural(changed.length, "cell")} changed since ` +
    `${changed.length === 1 ? "was" : "were"} left as ${changed.length === 1 ? "it is" : "they are"} (${listed}${more}).`
  );
}

/** ValuesPort on Office.js: cells through Excel.run, the saved cells in the document settings (Office.context.document.settings). */
export function createValuesPort(formulasIn: (scope: FormulaScope) => Promise<ScopedFormula[]>): ValuesPort {
  return {
    formulasIn,

    readCells: (cells) =>
      Excel.run(async (context) => {
        const sheets = new Map<string, Excel.Worksheet>();
        for (const c of cells) if (!sheets.has(c.sheet)) sheets.set(c.sheet, context.workbook.worksheets.getItemOrNullObject(c.sheet));
        await context.sync();
        const ranges = cells.map((c) => {
          const ws = sheets.get(c.sheet)!;
          return ws.isNullObject ? null : ws.getCell(c.row, c.col).load(["formulas", "values"]);
        });
        await context.sync();
        return ranges.map((r) => {
          if (!r) return { formula: "", value: null, sheetGone: true };
          const f = r.formulas[0][0];
          return { formula: typeof f === "string" && f.startsWith("=") ? f : "", value: r.values[0][0] };
        });
      }),

    writeValues: (cells) =>
      Excel.run(async (context) => {
        for (const c of cells) context.workbook.worksheets.getItem(c.sheet).getCell(c.row, c.col).values = [[c.value]];
        await context.sync();
      }),

    writeFormulas: (cells) =>
      Excel.run(async (context) => {
        for (const c of cells) context.workbook.worksheets.getItem(c.sheet).getCell(c.row, c.col).formulas = [[c.formula]];
        await context.sync();
      }),

    loadSaved: () => {
      const v = Office.context.document.settings.get(SAVED_KEY);
      return Array.isArray(v) ? (v as SavedCell[]) : [];
    },

    storeSaved: (cells) =>
      new Promise<void>((resolve, reject) => {
        const settings = Office.context.document.settings;
        if (cells.length) settings.set(SAVED_KEY, cells);
        else settings.remove(SAVED_KEY);
        settings.saveAsync((r) => (r.status === Office.AsyncResultStatus.Succeeded ? resolve() : reject(r.error)));
      }),
  };
}
